import { lazy, Suspense } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import { Role } from "@alzadrach/types";
import { useAuth } from "./auth/AuthContext";
import AppLayout from "./components/AppLayout";

// ---------------------------------------------------------------------------
// Lazy pages
// ---------------------------------------------------------------------------

const LoginPage = lazy(() => import("./pages/LoginPage"));
const ActivatePage = lazy(() => import("./pages/ActivatePage"));
const MemberDashboard = lazy(() => import("./pages/MemberDashboard"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const CuentasPage = lazy(() => import("./pages/CuentasPage"));
const EstatutosPage = lazy(() => import("./pages/EstatutosPage"));
const ReunionesPage = lazy(() => import("./pages/ReunionesPage"));
const ReunionNewPage = lazy(() => import("./pages/ReunionNewPage"));
const ReunionDetailPage = lazy(() => import("./pages/ReunionDetailPage"));

// Admin pages
const MembersPage = lazy(() => import("./pages/admin/MembersPage"));
const MemberNewPage = lazy(() => import("./pages/admin/MemberNewPage"));
const MemberDetailPage = lazy(() => import("./pages/admin/MemberDetailPage"));
const InvitadosPage = lazy(() => import("./pages/admin/InvitadosPage"));
const InvitadoNewPage = lazy(() => import("./pages/admin/InvitadoNewPage"));
const InvitadoDetailPage = lazy(() => import("./pages/admin/InvitadoDetailPage"));
const SeasonsPage = lazy(() => import("./pages/admin/SeasonsPage"));
const SeasonNewPage = lazy(() => import("./pages/admin/SeasonNewPage"));
const SeasonDetailPage = lazy(() => import("./pages/admin/SeasonDetailPage"));
const ImportPage = lazy(() => import("./pages/admin/ImportPage"));
const MovementsPage = lazy(() => import("./pages/admin/MovementsPage"));
const CuentaBancariaPage = lazy(() => import("./pages/admin/CuentaBancariaPage"));

// ---------------------------------------------------------------------------
// Guards
// ---------------------------------------------------------------------------

/** Redirect to /login if not authenticated. */
function RequireAuth() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="page-loader">Cargando…</div>;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <Outlet />;
}

/** Redirect a solo niños socio (dashboard + perfil only) away from the filà pages. */
export function RequireFullAccess() {
  const { user } = useAuth();
  if (user?.access === "ninos") {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
}

/** Redirect members away from admin-only routes. */
function RequireAdmin() {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="page-loader">Cargando…</div>;
  }

  if (!user || user.role !== Role.ADMIN) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}

// ---------------------------------------------------------------------------
// Loading fallback
// ---------------------------------------------------------------------------

function PageLoader() {
  return <div className="page-loader">Cargando…</div>;
}

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------

export const router = createBrowserRouter([
  {
    path: "/login",
    element: (
      <Suspense fallback={<PageLoader />}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    // Public: the activation link shared with a new socio (create a password).
    path: "/activar/:token",
    element: (
      <Suspense fallback={<PageLoader />}>
        <ActivatePage />
      </Suspense>
    ),
  },
  {
    element: <RequireAuth />,
    children: [
      {
        // Shared shell (sidebar + responsive drawer) for every authed page.
        element: <AppLayout />,
        children: [
      {
        // Member home — the main dashboard
        path: "/",
        element: (
          <Suspense fallback={<PageLoader />}>
            <MemberDashboard />
          </Suspense>
        ),
      },
      {
        // Cuentas, Estatutos, Reuniones: not for solo niños socios.
        element: <RequireFullAccess />,
        children: [
      {
        path: "/cuentas",
        element: (
          <Suspense fallback={<PageLoader />}>
            <CuentasPage />
          </Suspense>
        ),
      },
      // Old path kept as a redirect so PWA shortcuts / bookmarks still work.
      { path: "/gastos", element: <Navigate to="/cuentas" replace /> },
      {
        path: "/estatutos",
        element: (
          <Suspense fallback={<PageLoader />}>
            <EstatutosPage />
          </Suspense>
        ),
      },
      {
        path: "/reuniones",
        element: (
          <Suspense fallback={<PageLoader />}>
            <ReunionesPage />
          </Suspense>
        ),
      },
      {
        // Create page (admin only); declared before /reuniones/:id.
        element: <RequireAdmin />,
        children: [
          {
            path: "/reuniones/nueva",
            element: (
              <Suspense fallback={<PageLoader />}>
                <ReunionNewPage />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "/reuniones/:id",
        element: (
          <Suspense fallback={<PageLoader />}>
            <ReunionDetailPage />
          </Suspense>
        ),
      },
        ],
      },
      {
        path: "/perfil",
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProfilePage />
          </Suspense>
        ),
      },
      {
        element: <RequireAdmin />,
        children: [
          {
            // Admin section (renders inside the shared shell).
            path: "/admin",
            children: [
              {
                // Default redirect to /admin/socios
                index: true,
                element: <Navigate to="/admin/socios" replace />,
              },
              {
                path: "socios",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <MembersPage />
                  </Suspense>
                ),
              },
              {
                path: "socios/nuevo",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <MemberNewPage />
                  </Suspense>
                ),
              },
              {
                path: "socios/:id",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <MemberDetailPage />
                  </Suspense>
                ),
              },
              {
                path: "invitados",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <InvitadosPage />
                  </Suspense>
                ),
              },
              {
                path: "invitados/nuevo",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <InvitadoNewPage />
                  </Suspense>
                ),
              },
              {
                path: "invitados/:id",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <InvitadoDetailPage />
                  </Suspense>
                ),
              },
              {
                path: "temporadas",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <SeasonsPage />
                  </Suspense>
                ),
              },
              {
                path: "temporadas/nuevo",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <SeasonNewPage />
                  </Suspense>
                ),
              },
              {
                path: "temporadas/:id",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <SeasonDetailPage />
                  </Suspense>
                ),
              },
              {
                path: "importar",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ImportPage />
                  </Suspense>
                ),
              },
              {
                path: "movimientos",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <MovementsPage />
                  </Suspense>
                ),
              },
              {
                path: "cuenta-bancaria",
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <CuentaBancariaPage />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },
        ],
      },
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
]);
